import { useCallback, useId, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { Button } from './Button'
import { Dialog } from './Dialog'

export interface ConfirmDialogProps {
  readonly open: boolean
  /** The question, such as "Impersonate Jeanna Robb?". */
  readonly title: string
  /** What will happen, in plain words; read out as the dialog opens. */
  readonly children: ReactNode
  readonly confirmLabel: string
  /** `error` (the default) for an action that removes, ends or acts as someone; `primary` otherwise. */
  readonly tone?: 'error' | 'primary'
  /**
   * Takes the action. The confirm button shows busy and the dialog stays open until it settles;
   * the caller closes it, or shows why it failed, here through `problem` or on its page.
   */
  readonly onConfirm: () => void | Promise<void>
  /** Cancel, Escape, the close button or a click outside: nothing is done. */
  readonly onClose: () => void
  /** Shown above the description, such as why the action was refused. */
  readonly problem?: ReactNode
}

/**
 * Asks before an action that is hard to undo or reaches other people, in the mockups' dialog: the
 * question as the title, what will happen, then Cancel and the action. An alert dialog that starts
 * on Cancel, the choice that changes nothing, so Enter alone never takes the action.
 */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const { open, title, children, confirmLabel, tone = 'error', onConfirm, onClose, problem } = props
  const intl = useIntl()
  const descriptionId = useId()
  const cancelButton = useRef<HTMLButtonElement>(null)
  const [busy, setBusy] = useState(false)
  const running = useRef(false)

  // Not while the action runs, so its answer cannot land on a dialog the person thinks they left.
  const dismiss = useCallback(() => {
    if (!running.current) {
      onClose()
    }
  }, [onClose])
  const confirm = useCallback(async () => {
    if (running.current) {
      return
    }
    running.current = true
    setBusy(true)
    try {
      await onConfirm()
    } catch {
      // The caller shows its own failures; one it lets through leaves the dialog open to try again.
    } finally {
      running.current = false
      setBusy(false)
    }
  }, [onConfirm])
  const confirmClick = useCallback(() => void confirm(), [confirm])

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      title={title}
      size="xs"
      role="alertdialog"
      descriptionId={descriptionId}
      initialFocusRef={cancelButton}
      actions={
        <>
          <Button ref={cancelButton} variant="outlined" onClick={dismiss} disabled={busy}>
            {intl.formatMessage({ id: 'ui.confirm.cancel' })}
          </Button>
          <Button color={tone} onClick={confirmClick} busy={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {problem}
      <div id={descriptionId} className="flex flex-col gap-2 text-sm text-text-primary [overflow-wrap:anywhere]">
        {children}
      </div>
    </Dialog>
  )
}
